/**
 * <klaviyo-bis> — Back-in-stock inline form.
 *
 * Subscribes a shopper to Klaviyo's back-in-stock flow for the currently
 * selected variant via the client-side (public key) endpoint:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=PUBLIC_KEY
 *
 * Visibility is handled entirely in CSS off the theme's `.variant--soldout`
 * class on [data-form-wrapper], so this component only needs to:
 *   1. keep the target variant id in sync with the variant picker, and
 *   2. POST the subscription on submit.
 */
class KlaviyoBIS extends HTMLElement {
  connectedCallback() {
    this.company = this.getAttribute('company');
    this.revision = this.getAttribute('revision') || '2024-06-15';
    this.variantId = this.getAttribute('variant');
    this.form = this.querySelector('form');
    this.message = this.querySelector('[data-bis-message]');

    this.form.addEventListener('submit', this.subscribe.bind(this));

    // Keep the target variant in sync with the theme's variant picker.
    // `theme:variant:change` is dispatched on the product container (an ancestor
    // of this component) and bubbles up to document. Scope to our own product by
    // checking the dispatching element contains this component.
    document.addEventListener('theme:variant:change', (event) => {
      if (!event.detail || !event.detail.variant) return;
      if (!event.target.contains(this)) return;
      this.variantId = event.detail.variant.id;
    });
  }

  async subscribe(event) {
    event.preventDefault();

    const email = (this.form.email.value || '').trim();
    if (!email || !this.variantId || this.dataset.state === 'loading') return;

    this.setState('loading');

    try {
      const response = await fetch(
        'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(this.company),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: this.revision,
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email: email } } },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: '$shopify:::$default:::' + this.variantId,
                  },
                },
              },
            },
          }),
        }
      );

      this.setState(response.ok ? 'success' : 'error');
    } catch (error) {
      this.setState('error');
    }
  }

  setState(state) {
    this.dataset.state = state;
    if (this.message) {
      this.message.textContent = this.getAttribute('msg-' + state) || '';
    }
  }
}

customElements.define('klaviyo-bis', KlaviyoBIS);
